import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, MODELS, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, FileType2, FileText, Loader2, Sparkles, Plus, Trash2, BookMarked, Printer } from "lucide-react";
import { motion } from "framer-motion";
import RichEditor from "@/components/RichEditor";
// Voice-to-draft removed per product decision — all writing modules are keyboard-only.
import AIFullProject from "@/components/AIFullProject";

export default function PaperBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [templates, setTemplates] = useState({ templates: {}, help: {} });
  const [activeKey, setActiveKey] = useState("abstract");
  const [saving, setSaving] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiModel, setAiModel] = useState(0);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiPreview, setAiPreview] = useState("");

  useEffect(() => {
    api.get(`/papers/${id}`).then(r => setP(r.data)).catch(() => { toast.error("Not found"); nav("/app/papers"); });
    api.get("/papers/templates").then(r => setTemplates(r.data));
  }, [id, nav]);

  const setSection = (key, val) => setP(prev => ({ ...prev, sections: { ...prev.sections, [key]: val } }));

  const applyFullProject = ({ suggested_title, sections, references }) => {
    setP(prev => {
      const merged = { ...(prev.sections || {}) };
      Object.entries(sections || {}).forEach(([k, html]) => { if (html) merged[k] = html; });
      const nextRefs = (references || []).map(r => {
        const authors = (r.authors || []).join(", ");
        const parts = [authors, r.year ? `(${r.year})` : "", r.title || "", r.venue || "", r.doi ? `DOI: ${r.doi}` : (r.url || "")].filter(Boolean);
        return { citation: parts.join(". "), title: r.title || "", authors, year: r.year || "", doi: r.doi || "", url: r.url || "", venue: r.venue || "" };
      });
      return {
        ...prev,
        title: (!prev.title || prev.title === "Untitled paper") ? (suggested_title || prev.title) : prev.title,
        sections: merged,
        references: nextRefs.length ? nextRefs : (prev.references || []),
      };
    });
  };

  const save = async (silent=false) => {
    if (!p) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/papers/${id}`, {
        title: p.title, journal_template: p.journal_template, target_journal: p.target_journal,
        discipline: p.discipline, authors: p.authors, sections: p.sections, references: p.references, status: p.status,
      });
      setP(data);
      if (!silent) toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!p) return;
    const t = setTimeout(() => save(true), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [p?.title, p?.target_journal, p?.journal_template, p?.authors, p?.sections, p?.references]);

  const changeTemplate = (v) => {
    if (!p || v === p.journal_template) return;
    const prev = p.journal_template;
    const keys = templates.templates[v] || [];
    const next = { ...p, journal_template: v };
    const merged = { ...next.sections };
    for (const k of keys) if (!(k in merged)) merged[k] = "";
    next.sections = merged;
    setP(next);
    if (keys.length && !keys.includes(activeKey)) setActiveKey(keys[0]);
    // Alert: existing drafts were written in the previous publisher style.
    const hasContent = Object.values(p.sections || {}).some(v => (v || "").replace(/<[^>]+>/g," ").trim().length > 40);
    if (hasContent) {
      toast.warning(
        `Switched ${prev} → ${v}. Existing drafts still use ${prev} style. Click “Generate full paper” to re-draft with ${v} rules (citations, headings, abstract format all change).`,
        { duration: 9000 }
      );
    } else {
      toast.success(`Template set to ${v}. AI drafts will now strictly follow ${v} style.`);
    }
  };

  const runAI = async () => {
    const m = MODELS[aiModel];
    setAiStreaming(true); setAiPreview("");
    const token = localStorage.getItem("gf_token");
    try {
      const res = await fetch(`${API}/papers/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paper_id: id, section_key: activeKey, model_provider: m.provider, model_name: m.name, instruction: aiInstruction }),
      });
      if (!res.ok) throw new Error("AI failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) { acc += obj.delta; setAiPreview(acc); }
            if (obj.error) toast.error(obj.error);
          } catch { /* ignore */ }
        }
      }
    } catch (e) { toast.error(e.message || "AI failed"); }
    finally { setAiStreaming(false); }
  };
  const acceptAI = () => {
    const html = mdToHtml(aiPreview);
    setSection(activeKey, (p.sections?.[activeKey] || "") + html);
    setAiPreview(""); setAiInstruction("");
    toast.success("Inserted into draft");
  };

  const exportFile = async (kind) => {
    const url = `${API}/papers/${id}/export/${kind}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` }});
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${p.title || "paper"}.${kind}`;
    a.click();
    toast.success(`Downloaded ${kind.toUpperCase()}`);
  };

  if (!p) return <div className="p-10 text-muted-foreground">Loading paper…</div>;
  const keys = templates.templates[p.journal_template] || Object.keys(p.sections || {});

  return (
    <div className="min-h-screen paper">
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={()=>nav("/app/papers")} data-testid="paper-back"><ArrowLeft className="w-4 h-4 mr-1"/>Papers</Button>
          <Input value={p.title} onChange={(e)=>setP({...p, title: e.target.value})}
            className="flex-1 font-display text-xl border-none shadow-none focus-visible:ring-0 bg-transparent" data-testid="paper-title"/>
          <div className="flex items-center gap-2">
            {saving ? <span className="text-xs text-muted-foreground"><Loader2 className="inline w-3 h-3 mr-1 animate-spin"/>Saving…</span> : <span className="text-xs text-muted-foreground">All changes saved</span>}
            <AIFullProject
              module="paper"
              title={p.title}
              discipline={p.discipline || "Sciences"}
              testidPrefix="paper-fp"
              docId={id}
              saveDoc={()=>save(true)}
              buttonLabel="Generate full paper"
              dialogTitle={`Generate full paper (${p.journal_template} style)`}
              journalTemplate={p.journal_template}
              sectionKeys={templates.templates?.[p.journal_template] || []}
              sectionLabels={Object.fromEntries((templates.templates?.[p.journal_template] || []).map(k => [k, k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())]))}
              onApply={applyFullProject}
            />
            <Button variant="outline" size="sm" onClick={()=>save(false)}><Save className="w-4 h-4 mr-1"/>Save</Button>
            <Button size="sm" onClick={()=>exportFile("pdf")} className="rounded-full" data-testid="paper-export-pdf"><FileType2 className="w-4 h-4 mr-1"/>PDF</Button>
            <Button size="sm" variant="outline" onClick={()=>exportFile("docx")} data-testid="paper-export-docx"><FileText className="w-4 h-4 mr-1"/>DOCX</Button>
            <Button size="sm" variant="outline" onClick={()=>window.print()}><Printer className="w-4 h-4 mr-1"/>Print</Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-24 space-y-4">
            <div className="card-crisp rounded-lg p-4">
              <div className="overline mb-2">Template</div>
              <Select value={p.journal_template} onValueChange={changeTemplate}>
                <SelectTrigger data-testid="paper-template"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.keys(templates.templates).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-3"><Label className="text-xs">Target journal</Label>
                <Input value={p.target_journal || ""} onChange={(e)=>setP({...p, target_journal: e.target.value})} data-testid="paper-target"/>
              </div>
            </div>
            <div className="card-crisp rounded-lg p-3">
              <div className="overline mb-2 px-1">Outline</div>
              <ul className="space-y-1">
                {keys.map(k => {
                  const filled = ((p.sections?.[k] || "").replace(/<[^>]+>/g," ").trim()).length > 0;
                  const active = activeKey === k;
                  return (
                    <li key={k}>
                      <button onClick={()=>setActiveKey(k)} data-testid={`paper-outline-${k}`}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-[13px] flex items-center justify-between ${active?"bg-primary/10 text-primary":"hover:bg-secondary"}`}>
                        <span className="truncate">{k.replace(/_/g," ").replace(/\b\w/g, c=>c.toUpperCase())}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${filled?"bg-primary":"bg-muted-foreground/30"}`}/>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <AuthorsPanel p={p} setP={setP}/>
          </div>
        </aside>

        <section className="col-span-12 lg:col-span-6">
          <motion.div key={activeKey} initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} transition={{duration:0.2}}>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="overline">Section</div>
                <h2 className="font-display text-3xl capitalize">{activeKey.replace(/_/g," ")}</h2>
                <div className="text-xs text-muted-foreground mt-1">{templates.help?.[activeKey] || ""}</div>
              </div>
              <div className="text-xs text-muted-foreground">
                {(p.sections?.[activeKey] || "").replace(/<[^>]+>/g," ").trim().split(/\s+/).filter(Boolean).length} words
              </div>
            </div>
            <RichEditor value={p.sections?.[activeKey] || ""} onChange={(html)=>setSection(activeKey, html)} placeholder="Start writing — or ask AI on the right for a first draft."/>
          </motion.div>
        </section>

        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-24 space-y-4">
            <div className="card-crisp rounded-lg p-4">
              <div className="font-display text-xl flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-primary"/>AI co-author</div>
              <div className="space-y-3">
                <div><Label className="text-xs">Model</Label>
                  <Select value={String(aiModel)} onValueChange={(v)=>setAiModel(Number(v))}>
                    <SelectTrigger data-testid="paper-ai-model"><SelectValue/></SelectTrigger>
                    <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Instruction (optional)</Label>
                  <Textarea value={aiInstruction} onChange={(e)=>setAiInstruction(e.target.value)} placeholder="e.g., IEEE tone, emphasise novelty" className="min-h-[70px]"/>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 rounded-full" onClick={runAI} disabled={aiStreaming} data-testid="paper-ai-run">
                    {aiStreaming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Drafting…</> : <><Sparkles className="w-4 h-4 mr-2"/>Draft</>}
                  </Button>
                  {/* Voice-to-draft removed */}
                </div>
                {aiPreview && <div className="border border-primary/40 rounded-md p-3 bg-primary/5 max-h-[300px] overflow-auto whitespace-pre-wrap text-sm">{aiPreview}</div>}
                {aiPreview && !aiStreaming && (
                  <div className="flex gap-2">
                    <Button className="flex-1 rounded-full" onClick={acceptAI} data-testid="paper-ai-accept">Insert into draft</Button>
                    <Button variant="outline" className="rounded-full" onClick={()=>setAiPreview("")}>Discard</Button>
                  </div>
                )}
              </div>
            </div>

            <RefsPanel p={p} setP={setP}/>
          </div>
        </aside>
      </div>
    </div>
  );
}

function AuthorsPanel({ p, setP }) {
  const add = () => setP({ ...p, authors: [...(p.authors||[]), { id: crypto.randomUUID(), name:"", affiliation:"", email:"" }]});
  const upd = (i,k,v) => { const a=[...p.authors]; a[i]={...a[i], [k]:v}; setP({...p, authors:a}); };
  const del = i => setP({...p, authors: p.authors.filter((_,j)=>j!==i)});
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-lg">Authors</div>
        <Button size="sm" variant="ghost" onClick={add} data-testid="paper-authors-add"><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="space-y-2 max-h-[240px] overflow-auto">
        {(p.authors||[]).map((a,i)=>(
          <div key={a.id||i} className="border border-border rounded-md p-2 space-y-1.5">
            <Input className="h-8 text-xs" placeholder="Name" value={a.name} onChange={(e)=>upd(i,"name",e.target.value)}/>
            <Input className="h-8 text-xs" placeholder="Affiliation" value={a.affiliation} onChange={(e)=>upd(i,"affiliation",e.target.value)}/>
            <div className="flex gap-1">
              <Input className="h-8 text-xs flex-1" placeholder="Email" value={a.email} onChange={(e)=>upd(i,"email",e.target.value)}/>
              <button onClick={()=>del(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RefsPanel({ p, setP }) {
  const add = () => setP({ ...p, references: [...(p.references||[]), { id: crypto.randomUUID(), citation:"" }] });
  const upd = (i, v) => { const r=[...p.references]; r[i]={...r[i], citation: v}; setP({...p, references:r}); };
  const del = i => setP({...p, references: p.references.filter((_,j)=>j!==i)});
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-lg flex items-center gap-2"><BookMarked className="w-4 h-4 text-primary"/>References</div>
        <Button size="sm" variant="ghost" onClick={add}><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="space-y-2 max-h-[320px] overflow-auto">
        {(p.references||[]).map((r,i)=>(
          <div key={r.id||i} className="flex gap-1">
            <Textarea className="text-xs min-h-[50px]" placeholder="Full citation…" value={r.citation} onChange={(e)=>upd(i,e.target.value)}/>
            <button onClick={()=>del(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function mdToHtml(md) {
  if (!md) return "";
  const esc = s => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const lines = md.split(/\r?\n/); const out = []; let inList = null;
  const flush = () => { if (inList) { out.push(`</${inList}>`); inList = null; } };
  const inline = s => s.replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>").replace(/\*(.+?)\*/g,"<em>$1</em>").replace(/`([^`]+)`/g,"<code>$1</code>");
  for (const line of lines) {
    if (/^\s*#{3}\s+/.test(line)) { flush(); out.push(`<h3>${esc(line.replace(/^\s*###\s+/,""))}</h3>`); continue; }
    if (/^\s*#{2}\s+/.test(line)) { flush(); out.push(`<h2>${esc(line.replace(/^\s*##\s+/,""))}</h2>`); continue; }
    if (/^\s*#\s+/.test(line)) { flush(); out.push(`<h1>${esc(line.replace(/^\s*#\s+/,""))}</h1>`); continue; }
    if (/^\s*[-*]\s+/.test(line)) { if (inList!=="ul"){flush(); out.push("<ul>"); inList="ul";} out.push(`<li>${inline(esc(line.replace(/^\s*[-*]\s+/,"")))}</li>`); continue; }
    if (/^\s*\d+\.\s+/.test(line)) { if (inList!=="ol"){flush(); out.push("<ol>"); inList="ol";} out.push(`<li>${inline(esc(line.replace(/^\s*\d+\.\s+/,"")))}</li>`); continue; }
    if (line.trim()==="") { flush(); continue; }
    flush(); out.push(`<p>${inline(esc(line))}</p>`);
  }
  flush(); return out.join("\n");
}
